import React, { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';

const PERIOD_OPTIONS = [
  { value: 'all', label: 'Tüm Zamanlar' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'prev_month', label: 'Önceki Ay' },
  { value: 'this_year', label: 'Bu Yıl' },
  { value: 'prev_year', label: 'Önceki Yıl' },
  { value: 'custom', label: 'Özel Aralık' },
];

export function DateRangeFilter({ period, onPeriodChange, startDate, endDate, onStartDateChange, onEndDateChange, showLabel = true, className = '' }) {
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const handlePeriodChange = (value) => {
    onPeriodChange(value);
    if (value !== 'custom') {
      onStartDateChange(null);
      onEndDateChange(null);
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div>
        {showLabel && <Label>Dönem</Label>}
        <Select value={period} onValueChange={handlePeriodChange}>
          <SelectTrigger data-testid="period-filter">
            <CalendarIcon className="w-4 h-4 mr-2 flex-shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {period === 'custom' && (
        <div className="flex flex-col sm:flex-row gap-2">
          <Popover open={startOpen} onOpenChange={setStartOpen}>
            <PopoverTrigger asChild>
              <button
                data-testid="start-date-picker"
                className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <CalendarIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className={startDate ? 'text-gray-900' : 'text-gray-400'}>
                  {startDate ? format(startDate, 'dd MMM yyyy', { locale: tr }) : 'Başlangıç'}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => {
                  onStartDateChange(date);
                  setStartOpen(false);
                }}
                disabled={(date) => endDate ? date > endDate : false}
                locale={tr}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Popover open={endOpen} onOpenChange={setEndOpen}>
            <PopoverTrigger asChild>
              <button
                data-testid="end-date-picker"
                className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <CalendarIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className={endDate ? 'text-gray-900' : 'text-gray-400'}>
                  {endDate ? format(endDate, 'dd MMM yyyy', { locale: tr }) : 'Bitiş'}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => {
                  onEndDateChange(date);
                  setEndOpen(false);
                }}
                disabled={(date) => startDate ? date < startDate : false}
                locale={tr}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}

export { PERIOD_OPTIONS };
